import HomeNav from '@/pages/Home/HomeNav';
import { CopyableEmail } from "@/components/common/CopyableEmail";
import HomeFooter from '@/pages/Home/HomeFooter';
import GenieEmbed from '@/components/widget/GenieEmbed';
import { Card, CardContent } from '@/components/ui/card';

const CookiePolicy = () => {
  const lastUpdated = "January 16, 2025";

  return (
    <div className="min-h-screen w-full flex flex-col bg-[#F9FAFC]">
      <HomeNav />

      <main className="flex-1 flex flex-col items-center justify-center w-full">
        {/* Hero Section with Background Image */}
        <section className="relative w-full min-h-[400px] flex items-center justify-center text-white">
          {/* Background Image */}
          <div className="absolute inset-0 z-0">
            <img src="/insights-background.webp" alt="Cookie Policy background" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-purple-900/80 to-blue-900/80 z-10"></div>
          </div>

          {/* Content */}
          <div className="relative z-20 w-full max-w-screen-xl mx-auto px-8 py-20">
            <div className="text-center">
              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight">
                Cookie Policy
              </h1>
              <p className="text-xl md:text-2xl text-gray-200 max-w-3xl mx-auto">
                Learn how we use cookies to enhance your browsing experience.
              </p>
              <p className="text-lg text-gray-300 mt-4">
                Last Updated: <span className="font-semibold">{lastUpdated}</span>
              </p>
            </div>
          </div>
        </section>

        {/* Overview Card */}
        <section className="w-full py-16 md:py-20 bg-white">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <Card className="border-purple-200 bg-gradient-to-br from-purple-50 to-white shadow-lg">
              <CardContent className="pt-8 pb-8 px-6 sm:px-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">What Are Cookies?</h2>
                <p className="text-gray-700 leading-relaxed text-lg">
                  Cookies are small text files that are placed on your device when you visit our website. They help
                  us provide you with a better experience by remembering your preferences and understanding how you
                  use our services.
                </p>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Content Sections */}
        <section className="w-full py-12 bg-slate-50">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="space-y-12">
            {/* Section 1 */}
            <section>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                1. How We Use Cookies
              </h2>

              <div className="bg-white rounded-lg p-6 sm:p-8 border border-gray-200 shadow-sm">
                <p className="text-gray-700 text-base sm:text-lg mb-4">
                  Help Genie uses cookies and similar tracking technologies to:
                </p>
                <ul className="space-y-3 text-gray-700 text-base sm:text-lg">
                  <li className="flex gap-3">
                    <span className="text-purple-600 font-bold">•</span>
                    <span>Keep you signed in to your account</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-purple-600 font-bold">•</span>
                    <span>Remember your preferences and settings</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-purple-600 font-bold">•</span>
                    <span>Understand how you use our platform to improve our services</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-purple-600 font-bold">•</span>
                    <span>Analyze traffic and usage patterns</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-purple-600 font-bold">•</span>
                    <span>Protect against fraud and enhance security</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-purple-600 font-bold">•</span>
                    <span>Deliver personalized content and features</span>
                  </li>
                </ul>
              </div>
            </section>

            {/* Section 2 */}
            <section>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                2. Types of Cookies We Use
              </h2>

              <div className="bg-white rounded-lg p-6 border border-gray-200 mb-6">
                <h3 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-4">
                  2.1 Essential Cookies
                </h3>
                <p className="text-gray-700 text-base sm:text-lg mb-3">
                  These cookies are necessary for the website to function properly and cannot be disabled.
                </p>
                <ul className="space-y-3 text-gray-700 text-base sm:text-lg">
                  <li className="flex gap-3">
                    <span className="text-purple-600 font-bold">•</span>
                    <span><strong>Authentication:</strong> Keep you logged in during your session</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-purple-600 font-bold">•</span>
                    <span><strong>Security:</strong> Detect and prevent fraudulent activity</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-purple-600 font-bold">•</span>
                    <span><strong>Load Balancing:</strong> Distribute traffic across our servers</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white rounded-lg p-6 border border-gray-200 mb-6">
                <h3 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-4">
                  2.2 Functional Cookies
                </h3>
                <p className="text-gray-700 text-base sm:text-lg mb-3">
                  These cookies enable enhanced functionality and personalization.
                </p>
                <ul className="space-y-3 text-gray-700 text-base sm:text-lg">
                  <li className="flex gap-3">
                    <span className="text-purple-600 font-bold">•</span>
                    <span><strong>Preferences:</strong> Remember your language, region, and display settings</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-purple-600 font-bold">•</span>
                    <span><strong>User Interface:</strong> Store layout and customization choices</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-purple-600 font-bold">•</span>
                    <span><strong>Feature Access:</strong> Enable specific features you've activated</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white rounded-lg p-6 border border-gray-200 mb-6">
                <h3 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-4">
                  2.3 Analytics Cookies
                </h3>
                <p className="text-gray-700 text-base sm:text-lg mb-3">
                  These cookies help us understand how visitors interact with our website.
                </p>
                <ul className="space-y-3 text-gray-700 text-base sm:text-lg">
                  <li className="flex gap-3">
                    <span className="text-purple-600 font-bold">•</span>
                    <span><strong>Usage Analytics:</strong> Track pages visited and features used</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-purple-600 font-bold">•</span>
                    <span><strong>Performance Monitoring:</strong> Identify errors and performance issues</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-purple-600 font-bold">•</span>
                    <span><strong>User Behavior:</strong> Understand how users navigate and interact</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white rounded-lg p-6 sm:p-8 border border-gray-200 shadow-sm">
                <h3 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-4">
                  2.4 Marketing Cookies
                </h3>
                <p className="text-gray-700 text-base sm:text-lg mb-3">
                  These cookies are used to deliver relevant advertisements and track campaign effectiveness.
                </p>
                <ul className="space-y-3 text-gray-700 text-base sm:text-lg">
                  <li className="flex gap-3">
                    <span className="text-purple-600 font-bold">•</span>
                    <span><strong>Ad Targeting:</strong> Show you relevant ads based on your interests</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-purple-600 font-bold">•</span>
                    <span><strong>Campaign Tracking:</strong> Measure the effectiveness of our marketing</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-purple-600 font-bold">•</span>
                    <span><strong>Retargeting:</strong> Show ads to users who have visited our website</span>
                  </li>
                </ul>
              </div>
            </section>

            {/* Section 3 */}
            <section>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                3. Third-Party Cookies
              </h2>

              <div className="bg-white rounded-lg p-6 sm:p-8 border border-gray-200 shadow-sm">
                <p className="text-gray-700 text-base sm:text-lg mb-4">
                  We use services from trusted third parties that may set their own cookies:
                </p>
                <ul className="space-y-3 text-gray-700 text-base sm:text-lg">
                  <li className="flex gap-3">
                    <span className="text-purple-600 font-bold">•</span>
                    <span><strong>Supabase:</strong> Authentication and database services</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-purple-600 font-bold">•</span>
                    <span><strong>Stripe:</strong> Payment processing and billing</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-purple-600 font-bold">•</span>
                    <span><strong>Google Analytics:</strong> Website analytics and performance tracking</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-purple-600 font-bold">•</span>
                    <span><strong>Intercom/Support Tools:</strong> Customer support and chat services</span>
                  </li>
                </ul>
                <p className="text-gray-600 mt-4">
                  These third parties have their own privacy policies governing their use of cookies. We encourage
                  you to review their policies.
                </p>
              </div>
            </section>

            {/* Section 4 */}
            <section>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                4. Cookie Duration
              </h2>

              <div className="bg-white rounded-lg p-6 sm:p-8 border border-gray-200 shadow-sm">
                <p className="text-gray-700 text-base sm:text-lg mb-4">Cookies may be:</p>
                <ul className="space-y-3 text-gray-700 text-base sm:text-lg">
                  <li className="flex gap-3">
                    <span className="text-purple-600 font-bold">•</span>
                    <span>
                      <strong>Session Cookies:</strong> Temporary cookies that expire when you close your browser
                    </span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-purple-600 font-bold">•</span>
                    <span>
                      <strong>Persistent Cookies:</strong> Cookies that remain on your device for a set period or until manually deleted
                    </span>
                  </li>
                </ul>
              </div>
            </section>

            {/* Section 5 */}
            <section>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                5. Managing Your Cookie Preferences
              </h2>

              <div className="bg-white rounded-lg p-6 sm:p-8 border border-gray-200 shadow-sm">
                <p className="text-gray-700 text-base sm:text-lg mb-4">
                  You have several options to control and manage cookies:
                </p>

                <div className="space-y-4">
                  <div>
                    <h4 className="font-semibold text-lg text-gray-900 mb-2">Browser Settings</h4>
                    <p className="text-gray-600 mb-2">
                      Most web browsers allow you to control cookies through their settings. You can:
                    </p>
                    <ul className="space-y-2 text-gray-600 ml-4">
                      <li className="flex gap-3 text-gray-700">
                        <span className="text-purple-600 font-bold">•</span>
                        <span>Block all cookies</span>
                      </li>
                      <li className="flex gap-3 text-gray-700">
                        <span className="text-purple-600 font-bold">•</span>
                        <span>Allow only first-party cookies</span>
                      </li>
                      <li className="flex gap-3 text-gray-700">
                        <span className="text-purple-600 font-bold">•</span>
                        <span>Delete cookies after each browsing session</span>
                      </li>
                      <li className="flex gap-3 text-gray-700">
                        <span className="text-purple-600 font-bold">•</span>
                        <span>Receive notifications when cookies are set</span>
                      </li>
                    </ul>
                  </div>

                  <div>
                    <h4 className="font-semibold text-lg text-gray-900 mb-2">Browser-Specific Instructions</h4>
                    <ul className="space-y-2 text-gray-700 text-base sm:text-lg">
                      <li className="flex gap-3 text-gray-700">
                        <span className="text-purple-600 font-bold">•</span>
                        <span>
                          <strong>Chrome:</strong>{' '}
                          <a
                            href="https://support.google.com/chrome/answer/95647"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-purple-600 hover:text-purple-700 font-medium"
                          >
                            Cookie settings
                          </a>
                        </span>
                      </li>
                      <li className="flex gap-3 text-gray-700">
                        <span className="text-purple-600 font-bold">•</span>
                        <span>
                          <strong>Firefox:</strong>{' '}
                          <a
                            href="https://support.mozilla.org/en-US/kb/cookies-information-websites-store-on-your-computer"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-purple-600 hover:text-purple-700 font-medium"
                          >
                            Cookie settings
                          </a>
                        </span>
                      </li>
                      <li className="flex gap-3 text-gray-700">
                        <span className="text-purple-600 font-bold">•</span>
                        <span>
                          <strong>Safari:</strong>{' '}
                          <a
                            href="https://support.apple.com/guide/safari/manage-cookies-sfri11471/mac"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-purple-600 hover:text-purple-700 font-medium"
                          >
                            Cookie settings
                          </a>
                        </span>
                      </li>
                      <li className="flex gap-3 text-gray-700">
                        <span className="text-purple-600 font-bold">•</span>
                        <span>
                          <strong>Edge:</strong>{' '}
                          <a
                            href="https://support.microsoft.com/en-us/microsoft-edge/delete-cookies-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-purple-600 hover:text-purple-700 font-medium"
                          >
                            Cookie settings
                          </a>
                        </span>
                      </li>
                    </ul>
                  </div>

                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-4">
                    <p className="text-gray-700">
                      <strong>Important:</strong> Blocking or deleting cookies may impact your experience and prevent
                      certain features from working properly. Essential cookies cannot be disabled as they are required
                      for the platform to function.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Section 6 */}
            <section>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                6. Do Not Track Signals
              </h2>

              <div className="bg-white rounded-lg p-6 sm:p-8 border border-gray-200 shadow-sm">
                <p className="text-gray-700 text-base sm:text-lg">
                  Some browsers offer a "Do Not Track" (DNT) signal. Currently, there is no industry standard for
                  how to respond to DNT signals. We do not currently respond to DNT signals, but we respect your
                  privacy choices and provide other methods to control tracking as described in this policy.
                </p>
              </div>
            </section>

            {/* Section 7 */}
            <section>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                7. Updates to This Cookie Policy
              </h2>

              <div className="bg-white rounded-lg p-6 sm:p-8 border border-gray-200 shadow-sm">
                <p className="text-gray-700 text-base sm:text-lg">
                  We may update this Cookie Policy from time to time to reflect changes in technology, legislation,
                  or our practices. We will post any changes on this page and update the "Last Updated" date. We
                  encourage you to review this policy periodically.
                </p>
              </div>
            </section>

            {/* Section 8 */}
            <section>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                8. More Information
              </h2>

              <div className="bg-white rounded-lg p-6 sm:p-8 border border-gray-200 shadow-sm">
                <p className="text-gray-700 text-base sm:text-lg mb-4">
                  For more information about how we handle your data, please review our{' '}
                  <a href="/privacy" className="text-purple-600 hover:text-purple-700 font-medium">
                    Privacy Policy
                  </a>.
                </p>
                <p className="text-gray-700 text-base sm:text-lg mb-4">
                  If you have questions about our use of cookies, please contact us:
                </p>
                <ul className="space-y-2 text-gray-700 text-base sm:text-lg">
                  <li className="flex gap-3">
                    <span className="text-purple-600 font-bold">•</span>
                    <span>
                      By email:{' '}
                      <CopyableEmail email="support@helpgenie.ai" href="mailto:support@helpgenie.ai" className="text-genie-primary font-medium" />
                    </span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-purple-600 font-bold">•</span>
                    <span>
                      By visiting our support page:{' '}
                      <a href="https://helpgenie.ai/contact" className="text-purple-600 hover:text-purple-700 font-medium">
                        helpgenie.ai/contact
                      </a>
                    </span>
                  </li>
                </ul>
              </div>
            </section>
            </div>
          </div>
        </section>
      </main>

      <HomeFooter />
      <GenieEmbed position="bottom-right" />
    </div>
  );
};

export default CookiePolicy;
